import request from 'api/requestUtils'
import { GeoDetails } from './googleMaps'
import switchFunc from 'lib/switchFunc'
import { API } from '@luxuryescapes/lib-types'

const url = '/api/search/location-alert'

const levelMaps = switchFunc({
  country: 'country',
  administrative_area_level_1: 'state',
  colloquial_area: 'local',
  locality: 'local',
}, 'local')

const ApiToUiDestination = destination => ({
  placeId: destination.place_id,
  createdAt: destination.created_at,
  availableOffers: {
    state: destination.available_offers.administrative_area_level_1,
    continent: destination.available_offers.continent,
    country: destination.available_offers.country,
    local: destination.available_offers.local,
  },
  includesSurroundingAreas: destination.location_alert.level === 'colloquial_area',
  continent: destination.google_result.continent,
  country: destination.google_result.country,
  state: destination.google_result.administrative_area_level_1,
  local: destination.google_result.locality ?? destination.google_result.colloquial_area,
  locationAlert: {
    level: levelMaps(destination.location_alert.level),
    value: destination.location_alert.value,
    geocode: {
      lng: destination.location_alert.geocode.lng,
      lat: destination.location_alert.geocode.lat,
    },
  },
} as App.Destination)

const ApiToUiPopularRegion = alert => ({
  tag: alert.tag,
  imageId: alert.image_id,
  availableOffers: alert.available_offers,
} as App.PopularRegion)

export function getDestinationAlertList(regionCode: string) {
  return request.get<App.ApiResponse<any>>(`${url}?region=${regionCode}`, { credentials: 'include' })
    .then(result => result.location_alerts.map(ApiToUiDestination))
}

export function getPopularRegionAlertList(regionCode: string) {
  return request.get<App.ApiResponse<any>>(`${url}?region=${regionCode}`, { credentials: 'include' })
    .then(result => result.popular_locations)
}

export function addDestinationAlert(geoDetails: GeoDetails) {
  return request.post(url, geoDetails, { credentials: 'include' })
}

export function deleteDestinationAlerts(id: string) {
  return request.delete(`${url}/${id}?without_brand=1`, { credentials: 'include' })
}

export function getDestinationOffers(placeId: string, regionCode: string) {
  return request.get<App.ApiResponse<any>>(`/api/search/destination/${placeId}?region=${regionCode}`).then(ApiToUiDestination)
}

export function getFeaturedDestinations(regionCode: string) {
  return request.get<App.ApiResponse<Array<API.Search.Hotel.Place>>>(`/api/search/popular-places?region=${regionCode}`)
    .then(res => res.result)
}

export function addRegionAlert(destination) {
  return request.post<App.ApiResponse<any>, any>('/api/search/popular-location/tag?without_brand=1', { tag: destination }, { credentials: 'include' })
}

export function getPopularRegions(regionCode: string) {
  return request.get<App.ApiResponse<any>>(`/api/search/popular-region?region=${regionCode}`)
    .then(result => result.map(ApiToUiPopularRegion))
}

export function deletePopularRegionAlert(tag: string) {
  return request.delete(`/api/search/popular-location/tag/${tag}?without_brand=1`, { credentials: 'include' })
}

interface ServerGooglePrediction {
  description: string;
  place_id: string;
  reference: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  }
  types: Array<string>;
}

export function getGooglePlacePredictions(input: string) {
  return request.get<Array<ServerGooglePrediction>>(`/api/search/google-places/autocomplete/${input}`, { credentials: 'include' })
}

export function getTrendingDestinations(regionCode: string) {
  return request.get<App.ApiResponse<Array<App.TrendingDestination>>>(`/api/search/place/v1/trending?region=${regionCode}&limit=7`)
    .then(res => res.result)
}

export function getHotelTrendingDestinations(regionCode: string, limit: number, sortBy: App.HotelTrendingSortByOptions, exclude: string[], percentile: number) {
  return request.get<App.ApiResponse<Array<App.TrendingDestination>>>(`/api/search/hotel/v1/places/trending?region=${regionCode}&limit=${limit}&exclude=${exclude.join(',')}&sortBy=${sortBy}&percentile=${percentile}`)
    .then(res => res.result)
}

export function getHotelPopularHolidayTypes(regionCode: string) {
  return request.get<App.ApiResponse<Array<{ name: string }>>>(`/api/search/hotel/v1/popular/holiday-types?region=${regionCode}&limit=7`)
    .then(res => res.result)
}
